import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Newspaper, Calendar, MoreVertical, Play, Pencil, Trash2 } from "lucide-react";
import api from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

export default function DashboardPage() {
  const nav = useNavigate();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", base_slide_duration: 5 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journals");
      setJournals(data);
    } catch {
      toast.error("Failed to load journals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createJournal = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/journals", form);
      setOpen(false);
      setForm({ title: "", description: "", base_slide_duration: 5 });
      toast.success(`Created "${data.title}"`);
      nav(`/journals/${data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create journal");
    }
  };

  const removeJournal = async (id, title) => {
    if (!window.confirm(`Delete journal "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/journals/${id}`);
      toast.success("Journal deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <AppLayout>
      <div className="p-8 md:p-12 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="label-eyebrow mb-2">Workspace</div>
            <h1 className="font-display text-4xl font-bold">Journals</h1>
            <p className="text-zinc-400 mt-2 text-sm">All your ad journals — slideshow ready.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-journal-btn" className="bg-blue-600 hover:bg-blue-500">
                <Plus className="w-4 h-4 mr-2" /> New Journal
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50">
              <DialogHeader>
                <DialogTitle className="font-display">Create a new journal</DialogTitle>
              </DialogHeader>
              <form onSubmit={createJournal} className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-eyebrow">Title</Label>
                  <Input
                    data-testid="new-journal-title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                    placeholder="2026 Spring Auction Journal"
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Description</Label>
                  <Textarea
                    data-testid="new-journal-description"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    className="bg-zinc-950 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-eyebrow">Base slide duration (seconds)</Label>
                  <Input
                    data-testid="new-journal-duration"
                    type="number"
                    min={1}
                    step={0.5}
                    value={form.base_slide_duration}
                    onChange={(e) => setForm({ ...form, base_slide_duration: parseFloat(e.target.value) })}
                    className="bg-zinc-950 border-zinc-800"
                  />
                  <p className="text-xs text-zinc-500">Tier multipliers will scale this for each slide.</p>
                </div>
                <DialogFooter>
                  <Button data-testid="create-journal-submit" type="submit" className="bg-blue-600 hover:bg-blue-500">
                    Create journal
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-zinc-500">Loading…</div>
        ) : journals.length === 0 ? (
          <EmptyState onCreate={() => setOpen(true)} />
        ) : (
          <div data-testid="journals-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {journals.map((j) => (
              <div
                key={j.id}
                data-testid={`journal-card-${j.id}`}
                className="group relative bg-zinc-900 border border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <Newspaper className="w-5 h-5 text-blue-400" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`journal-menu-${j.id}`}
                        className="text-zinc-500 hover:text-zinc-50 p-1 rounded hover:bg-zinc-800"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                      <DropdownMenuItem onClick={() => nav(`/journals/${j.id}/edit`)} data-testid={`edit-${j.id}`}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => nav(`/journals/${j.id}/play`)} data-testid={`play-${j.id}`}>
                        <Play className="w-4 h-4 mr-2" /> Play slideshow
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => removeJournal(j.id, j.title)}
                        className="text-red-400 focus:text-red-300"
                        data-testid={`delete-${j.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Link to={`/journals/${j.id}/edit`} className="block mt-4">
                  <h3 className="font-display text-xl font-semibold tracking-tight leading-snug truncate">{j.title}</h3>
                  {j.description && (
                    <p className="text-sm text-zinc-400 line-clamp-2 mt-1">{j.description}</p>
                  )}
                </Link>
                <div className="mt-4 flex items-center justify-between text-xs text-zinc-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(j.updated_at).toLocaleDateString()}
                  </span>
                  <span className="font-mono">{j.base_slide_duration}s base</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-zinc-800 rounded-md p-16 text-center">
      <Newspaper className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
      <h3 className="font-display text-xl font-semibold mb-2">No journals yet</h3>
      <p className="text-sm text-zinc-400 mb-6 max-w-sm mx-auto">
        Start by creating a journal. You can bulk-import ads from a spreadsheet and folder of logos.
      </p>
      <Button onClick={onCreate} data-testid="empty-create-journal" className="bg-blue-600 hover:bg-blue-500">
        <Plus className="w-4 h-4 mr-2" /> Create your first journal
      </Button>
    </div>
  );
}
